import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { supabase } from '../../supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useProfileViews } from '../../hooks/useProfileViews';
import { getOrCreateConversation } from '../../services/chat';
import { sendDisqualificationEmail, sendNextStepEmail } from '../../services/emailService';

const CandidateProfile = () => {
    const { candidateId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { user } = useAuth();
    const { trackProfileView } = useProfileViews();
    const [candidateData, setCandidateData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [isDisqualified, setIsDisqualified] = useState(false);
    const [roleMatchPercentage, setRoleMatchPercentage] = useState(0);
    const [showDisqualifyModal, setShowDisqualifyModal] = useState(false);
    const [showRestoreStatusModal, setShowRestoreStatusModal] = useState(false);
    const [showNextStepConfirmModal, setShowNextStepConfirmModal] = useState(false);
    const [pendingRestoreStepIndex, setPendingRestoreStepIndex] = useState(null);
    const [restoreStatusError, setRestoreStatusError] = useState('');
    const [disqualifyReason, setDisqualifyReason] = useState('');
    const [sendNotification, setSendNotification] = useState(true);
    const [applicationData, setApplicationData] = useState(null);
    const [jobListingData, setJobListingData] = useState(null);
    const hasLoadedInitialStatus = React.useRef(false);
    
    // Get jobId from URL params, location state, or search params
    const jobIdFromUrl = searchParams.get('jobId');
    const jobIdFromState = location.state?.jobId;
    const jobId = jobIdFromUrl || jobIdFromState || null;

    // Load current step from database only once on initial mount (only if jobId is present)
    useEffect(() => {
        if (!candidateId || !user?.id || !jobId || hasLoadedInitialStatus.current) return;
        
        hasLoadedInitialStatus.current = true;
        
        const loadInitialStatus = async () => {
            try {
                let jobIds = [];
                
                // If we have a specific jobId, use it; otherwise get all job listings for this employer
                if (jobId) {
                    jobIds = [jobId];
                } else {
                    // Get job listings for this employer
                    const { data: jobListings, error: jobsError } = await supabase
                        .from('internal_job_listings')
                        .select('id')
                        .eq('employer_id', user.id);
                    
                    if (jobsError) {
                        return;
                    }
                    
                    jobIds = jobListings?.map(j => j.id) || [];
                }
                
                if (jobIds.length === 0) {
                    // No jobs for this employer, start at step 0
                    setCurrentStepIndex(0);
                    setIsDisqualified(false);
                    return;
                }
                
                const { data: applications, error: appError } = await supabase
                    .from('applications')
                    .select('id, status, stage')
                    .eq('user_id', candidateId)
                    .in('job_listing_id', jobIds)
                    .order('application_date', { ascending: false })
                    .limit(1);

                if (appError) {
                    return;
                }

                if (applications && applications.length > 0) {
                    const app = applications[0];
                    const status = app.status || 'applied';
                    
                    // Map status to step index
                    const statusToStepMap = {
                        'applied': 0,
                        'screening': 1,
                        'interview': 2,
                        'assessment': 3,
                        'hired': 4,
                        'rejected': -1 // Disqualified
                    };

                    const stepIndex = statusToStepMap[status] ?? 0;
                    setCurrentStepIndex(stepIndex);
                    setIsDisqualified(status === 'rejected');
                } else {
                    // No application found, start at step 0
                    setCurrentStepIndex(0);
                    setIsDisqualified(false);
                }
            } catch (err) {
                // Error loading candidate status
            }
        };
        
        loadInitialStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [candidateId, user?.id]); // Only run once when candidateId or user.id is available

    useEffect(() => {
        if (candidateId) {
            fetchCandidateProfile();
        } else {
            navigate('/employer/candidates');
        }
    }, [candidateId, navigate]);

    // Fetch application data when jobId is present
    useEffect(() => {
        if (candidateId && jobId) {
            fetchApplicationData();
        }
    }, [candidateId, jobId]);

    // Calculate profile completeness/match percentage
    const calculateProfileMatch = (profileData) => {
        if (!profileData) return 0;
        
        let score = 0;
        const checks = [
            // Basic info
            profileData.fullName && profileData.fullName !== 'Employee User',
            profileData.location && profileData.location !== 'Location not specified',
            profileData.aboutText && profileData.aboutText !== 'Profile information not available',
            
            // Skills and experience
            profileData.skills?.length > 0,
            profileData.workExperiences?.length > 0,
            profileData.educations?.length > 0,
            
            // Additional info
            profileData.languages?.length > 0,
            profileData.linkedinUrl,
            profileData.hpcsaRegNo,
            
            // Roles and certifications
            profileData.rolesOpenTo?.length > 0,
            profileData.certifications?.length > 0
        ];
        
        const completedChecks = checks.filter(check => check).length;
        score = Math.round((completedChecks / checks.length) * 100);
        
        return Math.max(0, Math.min(100, score)); // Return actual score between 0-100%
    };

    // Calculate match percentage when candidate data is loaded
    useEffect(() => {
        if (candidateData) {
            const matchPercentage = calculateProfileMatch(candidateData);
            setRoleMatchPercentage(matchPercentage);
        }
    }, [candidateData]);

    const fetchCandidateProfile = async () => {
        try {
            setLoading(true);
            setError(null);

            // POPI COMPLIANCE: Require a jobId to view candidate profiles
            if (!jobId) {
                throw new Error('Job listing is required to view candidate profiles. Please select a job listing.');
            }

            // SECURITY: Verify the viewing user is an employer before allowing access
            if (!user?.id) {
                throw new Error('You must be logged in to view candidate profiles');
            }

            // POPI COMPLIANCE: Verify the job listing belongs to this employer
            const { data: jobListing, error: jobError } = await supabase
                .from('internal_job_listings')
                .select('id, employer_id')
                .eq('id', jobId)
                .eq('employer_id', user.id)
                .single();

            if (jobError || !jobListing) {
                throw new Error('Job listing not found or access denied.');
            }

            // POPI COMPLIANCE: Verify the candidate has applied to this specific job listing
            const { data: application, error: appError } = await supabase
                .from('applications')
                .select('id, user_id, job_listing_id, status, stage')
                .eq('user_id', candidateId)
                .eq('job_listing_id', jobId)
                .limit(1)
                .single();

            if (appError || !application) {
                throw new Error('This candidate has not applied to this job listing. You can only view profiles of candidates who have applied.');
            }

            // First, verify this user is an employee and get their profile info
            const { data: userProfile, error: profileError } = await supabase
                .from('users_profiles')
                .select(`
                    profileid, role, email, firstname, lastname, headline,
                    profession, location_city_country, profile_about,
                    profile_image_url, linkedinurl, jobsearchstatus,
                    hpcsa_reg_no, hpcsa_verified, car_license, skills, languages, 
                    work_experiences, educations, certifications,
                    selected_roles, employment_types, profile_visibility
                `)
                .eq('profileid', candidateId)
                .eq('role', 'Employee')
                .single();

            if (profileError || !userProfile) {
                throw new Error('Candidate not found or not an employee');
            }

            // Check profile visibility settings
            const visibility = userProfile.profile_visibility;
            if (visibility === 'hidden') {
                throw new Error('This profile is hidden and cannot be viewed');
            }
            
            // Since the candidate has applied, they've consented to share their profile
            // No need for additional visibility checks beyond the application verification

            // Create candidate data from users_profiles
            const firstName = userProfile.firstname || '';
            const lastName = userProfile.lastname || '';
            const fullName = firstName && lastName ? `${firstName} ${lastName}` : firstName || lastName || 'Employee User';
            
            // Parse JSON fields
            const skills = userProfile.skills ? JSON.parse(userProfile.skills) : [];
            const languages = userProfile.languages ? JSON.parse(userProfile.languages) : [];
            const workExperiences = userProfile.work_experiences ? JSON.parse(userProfile.work_experiences) : [];
            const educations = userProfile.educations ? JSON.parse(userProfile.educations) : [];
            const certifications = userProfile.certifications ? JSON.parse(userProfile.certifications) : [];
            const selectedRoles = userProfile.selected_roles ? JSON.parse(userProfile.selected_roles) : [];
            
            // Get profile image URL - check if it's already a full URL or just a path
            let profileImageUrl = '';
            if (userProfile.profile_image_url) {
                // If it's already a full URL (starts with http), use it directly
                if (userProfile.profile_image_url.startsWith('http')) {
                    profileImageUrl = userProfile.profile_image_url;
                } else {
                    // If it's just a path, generate the public URL
                    const { data: imageData } = supabase.storage
                        .from('profile-images')
                        .getPublicUrl(userProfile.profile_image_url);
                    profileImageUrl = imageData.publicUrl;
                }
            }
            
            const processedData = {
                // Basic info
                fullName: fullName,
                headline: userProfile.headline || '',
                email: userProfile.email || 'email@example.com',
                location: userProfile.location_city_country || 'Location not specified',
                linkedinUrl: userProfile.linkedinurl || '',
                profileImageUrl: profileImageUrl,
                hpcsaRegNo: (userProfile.hpcsa_reg_no || '').trim(),
                hpcsaVerified: (() => {
                    const value = userProfile.hpcsa_verified;
                    return value === true || value === 'true' || value === 1 || value === '1' || value === 't' || value === 'T';
                })(),
                
                // About text
                aboutText: userProfile.profile_about || 'Profile information not available',
                
                // Skills
                skills: skills,
                
                // Work experiences
                workExperiences: workExperiences,
                
                // Education
                educations: educations,
                
                // Certifications
                certifications: certifications,
                
                // Languages
                languages: languages,
                
                // Roles open to (from onboarding)
                rolesOpenTo: selectedRoles,
            };

            setCandidateData(processedData);
            
            // Track profile view
            if (user && user.id !== candidateId) {
                const context = {
                    source: 'employer_candidate_search',
                    profileRole: 'Employee',
                    referrer: document.referrer,
                    userAgent: navigator.userAgent
                };
                
                trackProfileView(candidateId, context);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchApplicationData = async () => {
        if (!candidateId || !jobId) {
            return;
        }

        try {
            // Fetch application data
            const { data: application, error: appError } = await supabase
                .from('applications')
                .select('*')
                .eq('user_id', candidateId)
                .eq('job_listing_id', jobId)
                .single();

            if (appError) {
                if (appError.code === 'PGRST116') {
                    // Not found - this is okay, maybe they haven't applied yet
                } else {
                    // Error fetching application
                }
                return;
            }

            if (application) {
                setApplicationData(application);
            }

            // Fetch job listing data to get screening questions
            const { data: jobListing, error: jobError } = await supabase
                .from('internal_job_listings')
                .select('screening_questions_list, screening_questions, drivers_license_required, visa_sponsorship_offered')
                .eq('id', jobId)
                .single();

            if (jobError) {
                console.error('Error fetching job listing:', jobError);
                return;
            }

            if (jobListing) {
                setJobListingData(jobListing);
            }
        } catch (err) {
            console.error('Error fetching application data:', err);
        }
    };

    const EditIcon = () => (
        <svg className="w-4 h-4 text-gray-500 hover:text-gray-700 cursor-pointer" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
    );

    // eslint-disable-next-line no-unused-vars
    const AddIcon = () => (
        <svg className="w-4 h-4 text-gray-500 hover:text-gray-700 cursor-pointer" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
    );

    const LinkedInIcon = () => (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
        </svg>
    );

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Profile Not Available</h2>
                    <p className="text-gray-600 mb-6">{error}</p>
                    <button 
                        type="button"
                        onClick={() => navigate(-1)}
                        className="bg-black text-white px-6 py-2 rounded-lg hover:bg-gray-800 transition-colors"
                    >
                        Back to Candidates
                    </button>
                </div>
            </div>
        );
    }

    if (!candidateData) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">No Profile Data</h2>
                    <p className="text-gray-600 mb-6">This candidate's profile data could not be loaded.</p>
                    <button 
                        type="button"
                        onClick={() => navigate(-1)}
                        className="bg-black text-white px-6 py-2 rounded-lg hover:bg-gray-800 transition-colors"
                    >
                        Back to Candidates
                    </button>
                </div>
            </div>
        );
    }

    const isOpenToWork = true; // placeholder visual cue; can be computed from jobsearchstatus

    const steps = [
        { key: 'application', label: 'Application', status: 'applied', stage: 'Applications' },
        { key: 'shortlist', label: 'Shortlist', status: 'screening', stage: 'Shortlist' },
        { key: 'interview', label: 'Interview', status: 'interview', stage: 'Interview' },
        { key: 'test', label: 'Test', status: 'assessment', stage: 'Test' },
        { key: 'hired', label: 'Hired', status: 'hired', stage: 'Hired' }
    ];

    const handleRestoreFromDisqualified = async (targetStepIndex) => {
        if (!jobId) {
            alert('Cannot update hiring step without a job listing. Please view this candidate from a specific job listing.');
            return;
        }

        const step = steps[targetStepIndex];
        if (!step) return;
        setRestoreStatusError('');
        setPendingRestoreStepIndex(targetStepIndex);
        setShowRestoreStatusModal(true);
        return;
    };

    const confirmRestoreFromDisqualified = async (notifyApplicant) => {
        if (pendingRestoreStepIndex === null || pendingRestoreStepIndex === undefined) {
            setShowRestoreStatusModal(false);
            return;
        }

        if (!jobId) {
            setRestoreStatusError('Job listing is required to update candidate status.');
            return;
        }

        const targetStepIndex = pendingRestoreStepIndex;
        const step = steps[targetStepIndex];
        if (!step) {
            setShowRestoreStatusModal(false);
            return;
        }

        try {
            const jobListingId = jobId;

            const { data: existingApps, error: findError } = await supabase
                .from('applications')
                .select('id, stage')
                .eq('user_id', candidateId)
                .eq('job_listing_id', jobListingId)
                .limit(1);

            if (findError) {
                setRestoreStatusError('Failed to update status. Please try again.');
                return;
            }

            const previousStage = existingApps?.[0]?.stage || applicationData?.stage || (isDisqualified ? 'Rejected' : (steps[currentStepIndex]?.stage || 'Applications'));

            if (existingApps && existingApps.length > 0) {
                const applicationId = existingApps[0].id;
                const { error: updateError } = await supabase
                    .from('applications')
                    .update({
                        status: step.status,
                        stage: step.stage,
                        last_updated: new Date().toISOString()
                    })
                    .eq('id', applicationId)
                    .eq('job_listing_id', jobListingId)
                    .eq('user_id', candidateId);

                if (updateError) {
                    setRestoreStatusError('Failed to update status. Please try again.');
                    return;
                }
            } else {
                const { error: createError } = await supabase
                    .from('applications')
                    .insert({
                        user_id: candidateId,
                        job_listing_id: jobListingId,
                        status: step.status,
                        stage: step.stage,
                        application_date: new Date().toISOString(),
                        last_updated: new Date().toISOString()
                    });

                if (createError) {
                    // If create fails, still allow UI update (non-blocking)
                }
            }

            setCurrentStepIndex(targetStepIndex);
            setIsDisqualified(false);
            setShowRestoreStatusModal(false);
            setPendingRestoreStepIndex(null);

            // Send next step email only when employer opted in
            if (
                notifyApplicant &&
                step.stage !== 'Applications' &&
                step.stage !== 'Rejected' &&
                previousStage !== step.stage
            ) {
                try {
                    const { data: candidateRow } = await supabase
                        .from('users_profiles')
                        .select('email, firstname, lastname')
                        .eq('profileid', candidateId)
                        .single();

                    const { data: jobRow } = await supabase
                        .from('internal_job_listings')
                        .select('title, company_name, id')
                        .eq('id', jobListingId)
                        .single();

                    if (candidateRow && jobRow) {
                        const candidateName = `${candidateRow.firstname || ''} ${candidateRow.lastname || ''}`.trim() || 'Candidate';
                        const jobLink = `https://doctorsjobs.io/job/${jobRow.id}`;
                        await sendNextStepEmail(
                            candidateRow.email,
                            candidateName,
                            jobRow.title,
                            jobRow.company_name,
                            step.stage,
                            jobLink,
                            jobRow.id
                        );
                    }
                } catch (emailError) {
                    // Don't fail the status update if email fails
                }
            }
        } catch (err) {
            setRestoreStatusError('Failed to update status. Please try again.');
        }
    };

    const handleNextStep = () => {
        if (!jobId) {
            alert('Cannot update hiring step without a job listing. Please view this candidate from a specific job listing.');
            return;
        }
        if (currentStepIndex >= steps.length - 1) {
            return;
        }
        setShowNextStepConfirmModal(true);
    };

    const confirmAdvanceNextStep = (notifyApplicant) => {
        setShowNextStepConfirmModal(false);

        setCurrentStepIndex((prevIndex) => {
            if (prevIndex >= steps.length - 1) {
                return prevIndex;
            }

            const nextStepIndex = prevIndex + 1;
            const nextStep = steps[nextStepIndex];

            (async () => {
                try {
                    const jobListingId = jobId;

                    const { data: existingApps, error: findError } = await supabase
                        .from('applications')
                        .select('id, user_id, job_listing_id')
                        .eq('user_id', candidateId)
                        .eq('job_listing_id', jobListingId)
                        .limit(1);

                    if (findError) {
                        setCurrentStepIndex(prevIndex);
                        return;
                    }

                    if (existingApps && existingApps.length > 0) {
                        const applicationId = existingApps[0].id;
                        const { error: updateError } = await supabase
                            .from('applications')
                            .update({
                                status: nextStep.status,
                                stage: nextStep.stage,
                                last_updated: new Date().toISOString()
                            })
                            .eq('id', applicationId)
                            .eq('job_listing_id', jobListingId)
                            .eq('user_id', candidateId);

                        if (updateError) {
                            setCurrentStepIndex(prevIndex);
                            alert('Failed to update step. Please try again.');
                            return;
                        }

                        if (notifyApplicant) {
                            try {
                                const { data: candidateData } = await supabase
                                    .from('users_profiles')
                                    .select('email, firstname, lastname')
                                    .eq('profileid', candidateId)
                                    .single();

                                const { data: jobData } = await supabase
                                    .from('internal_job_listings')
                                    .select('title, company_name, id')
                                    .eq('id', jobListingId)
                                    .single();

                                if (candidateData && jobData) {
                                    const candidateName = `${candidateData.firstname || ''} ${candidateData.lastname || ''}`.trim() || 'Candidate';
                                    const jobLink = `https://doctorsjobs.io/job/${jobData.id}`;
                                    await sendNextStepEmail(
                                        candidateData.email,
                                        candidateName,
                                        jobData.title,
                                        jobData.company_name,
                                        nextStep.stage,
                                        jobLink,
                                        jobData.id
                                    );
                                }
                            } catch (emailError) {
                                // Don't fail the step update if email fails
                            }
                        }
                    } else {
                        const { error: createError } = await supabase
                            .from('applications')
                            .insert({
                                user_id: candidateId,
                                job_listing_id: jobListingId,
                                status: nextStep.status,
                                stage: nextStep.stage,
                                application_date: new Date().toISOString(),
                                last_updated: new Date().toISOString()
                            });

                        if (createError) {
                            // Could not create application record, updating UI only
                        }

                        if (notifyApplicant && !createError) {
                            try {
                                const { data: candidateData } = await supabase
                                    .from('users_profiles')
                                    .select('email, firstname, lastname')
                                    .eq('profileid', candidateId)
                                    .single();

                                const { data: jobData } = await supabase
                                    .from('internal_job_listings')
                                    .select('title, company_name, id')
                                    .eq('id', jobListingId)
                                    .single();

                                if (candidateData && jobData) {
                                    const candidateName = `${candidateData.firstname || ''} ${candidateData.lastname || ''}`.trim() || 'Candidate';
                                    const jobLink = `https://doctorsjobs.io/job/${jobData.id}`;
                                    await sendNextStepEmail(
                                        candidateData.email,
                                        candidateName,
                                        jobData.title,
                                        jobData.company_name,
                                        nextStep.stage,
                                        jobLink,
                                        jobData.id
                                    );
                                }
                            } catch (emailError) {
                                // Don't fail the step update if email fails
                            }
                        }
                    }
                } catch (err) {
                    setCurrentStepIndex(prevIndex);
                    alert('Failed to update step. Please try again.');
                }
            })();

            return nextStepIndex;
        });

        setIsDisqualified(false);
    };

    const handleAcceptApplicant = async () => {
        // Only allow if jobId is present
        if (!jobId) {
            alert('Cannot accept applicant without a job listing. Please view this candidate from a specific job listing.');
            return;
        }

        try {
            const jobListingId = jobId;
            
            // Find existing application - must match both user_id AND job_listing_id
            const { data: existingApps, error: findError } = await supabase
                .from('applications')
                .select('id, user_id, job_listing_id, status')
                .eq('user_id', candidateId)
                .eq('job_listing_id', jobListingId)
                .limit(1);

            if (findError) {
                alert('Failed to accept applicant. Please try again.');
                return;
            }

            if (existingApps && existingApps.length > 0) {
                // Update to screening status (shortlist)
                const applicationId = existingApps[0].id;
                const { error: updateError } = await supabase
                    .from('applications')
                    .update({
                        status: 'screening',
                        stage: 'Shortlist',
                        last_updated: new Date().toISOString()
                    })
                    .eq('id', applicationId)
                    .eq('job_listing_id', jobListingId)
                    .eq('user_id', candidateId);

                if (updateError) {
                    alert('Failed to accept applicant. Please try again.');
                    return;
                }
                
                // Update UI state
                setCurrentStepIndex(1); // Set to screening step
                setIsDisqualified(false);
            } else {
                // Create new application with screening status
                const { error: createError } = await supabase
                    .from('applications')
                    .insert({
                        user_id: candidateId,
                        job_listing_id: jobListingId,
                        status: 'screening',
                        stage: 'Shortlist',
                        application_date: new Date().toISOString(),
                        last_updated: new Date().toISOString()
                    });

                if (createError) {
                    alert('Failed to accept applicant. Please try again.');
                    return;
                }
                
                // Update UI state
                setCurrentStepIndex(1); // Set to screening step
                setIsDisqualified(false);
            }
        } catch (err) {
            alert('Failed to accept applicant. Please try again.');
        }
    };

    const handleDeclineApplicant = () => {
        // Only allow if jobId is present
        if (!jobId) {
            alert('Cannot decline applicant without a job listing. Please view this candidate from a specific job listing.');
            return;
        }
        
        // Open the disqualify modal (modal itself serves as confirmation)
        setShowDisqualifyModal(true);
        setDisqualifyReason('Experience suitability is not suitable');
        setSendNotification(true);
    };

    const handleDisqualifyConfirm = async () => {
        if (!disqualifyReason) {
            alert('Please select a reason for disqualification.');
            return;
        }

        try {
            const jobListingId = jobId;
            
            // Find existing application - must match both user_id AND job_listing_id
            const { data: existingApps, error: findError } = await supabase
                .from('applications')
                .select('id, user_id, job_listing_id')
                .eq('user_id', candidateId)
                .eq('job_listing_id', jobListingId)
                .limit(1);

            if (findError) {
                alert('Failed to disqualify applicant. Please try again.');
                return;
            }

            const updateData = {
                status: 'rejected',
                stage: 'Rejected',
                last_updated: new Date().toISOString(),
                rejection_reason: disqualifyReason,
                send_notification: sendNotification
            };

            if (existingApps && existingApps.length > 0) {
                // Update to rejected status - use both id AND job_listing_id to ensure we only update the correct one
                const applicationId = existingApps[0].id;
                const { error: updateError } = await supabase
                    .from('applications')
                    .update(updateData)
                    .eq('id', applicationId)
                    .eq('job_listing_id', jobListingId)
                    .eq('user_id', candidateId);

                if (updateError) {
                    alert('Failed to disqualify applicant. Please try again.');
                    return;
                }
            } else {
                // Create new application with rejected status
                const { error: createError } = await supabase
                    .from('applications')
                    .insert({
                        user_id: candidateId,
                        job_listing_id: jobListingId,
                        status: 'rejected',
                        stage: 'Rejected',
                        application_date: new Date().toISOString(),
                        last_updated: new Date().toISOString(),
                        rejection_reason: disqualifyReason,
                        send_notification: sendNotification
                    });

                if (createError) {
                    alert('Failed to disqualify applicant. Please try again.');
                    return;
                }
            }

            // Send email notification if sendNotification is true
            if (sendNotification) {
                try {
                    // Get candidate email and name
                    const { data: candidateData } = await supabase
                        .from('users_profiles')
                        .select('email, firstname, lastname')
                        .eq('profileid', candidateId)
                        .single();
                    
                    // Get job details
                    const { data: jobData } = await supabase
                        .from('internal_job_listings')
                        .select('title, company_name')
                        .eq('id', jobListingId)
                        .single();
                    
                    if (candidateData && jobData) {
                        const candidateName = `${candidateData.firstname || ''} ${candidateData.lastname || ''}`.trim() || 'Candidate';
                        await sendDisqualificationEmail(
                            candidateData.email,
                            candidateName,
                            jobData.title,
                            jobData.company_name,
                            disqualifyReason
                        );
                    }
                } catch (emailError) {
                    // Don't fail the disqualification if email fails
                }
            }

            setIsDisqualified(true);
            setCurrentStepIndex(-1); // Reset to show no step as active
            setShowDisqualifyModal(false);
            
            // Refresh the page or update UI to reflect changes
            window.location.reload();
        } catch (err) {
            alert('Failed to disqualify applicant. Please try again.');
        }
    };

    const handleSendMessage = async () => {
        if (!user?.id || !candidateId) return;
        try {
            const convo = await getOrCreateConversation(user.id, candidateId);
            navigate('/employer/messages', { state: { openConversationId: convo.id } });
        } catch (e) {
            // Failed to start conversation
        }
    };

    return (
        <>
        <style>{`
            @media (max-width: 1024px) {
                .employer-candidate-page {
                    padding: 1rem !important;
                }
                .employer-candidate-container {
                    padding: 0 !important;
                }
                .employer-candidate-back {
                    margin-bottom: 1rem !important;
                }
                .employer-candidate-header {
                    padding: 1.25rem !important;
                }
                .employer-candidate-header-top {
                    flex-direction: column !important;
                    align-items: flex-start !important;
                }
                .employer-candidate-header-actions {
                    width: 100% !important;
                    flex-direction: column !important;
                    align-items: stretch !important;
                    gap: 0.5rem !important;
                }
                .employer-candidate-header-actions button {
                    width: 100% !important;
                }
                .employer-candidate-hiring-grid {
                    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
                    gap: 0.75rem !important;
                }
                .employer-candidate-main-grid {
                    grid-template-columns: 1fr !important;
                    gap: 1rem !important;
                }
                .employer-candidate-card {
                    padding: 1.25rem !important;
                }
                .employer-candidate-card-actions {
                    margin-left: 0 !important;
                    margin-top: 1rem !important;
                    width: 100% !important;
                    flex-direction: column !important;
                    gap: 0.5rem !important;
                }
                .employer-candidate-card-actions button {
                    width: 100% !important;
                }
            }
        `}</style>
        <div className="min-h-screen bg-gray-50 py-8 employer-candidate-page">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 employer-candidate-container">
                <button 
                    type="button"
                    onClick={() => navigate(-1)}
                    className="flex items-center text-gray-600 hover:text-gray-900 mb-4 employer-candidate-back"
                >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Back to Candidates
                </button>

                {/* Top Header */}
                <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6 employer-candidate-header">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 employer-candidate-header-top">
                        <div className="flex items-center gap-4">
                            <div className="w-16 h-16 bg-gray-200 rounded-full overflow-hidden flex items-center justify-center">
                                {candidateData.profileImageUrl ? (
                                    <img src={candidateData.profileImageUrl} alt="Profile" className="w-full h-full object-cover" />
                                ) : (
                                    <span className="text-lg font-medium text-gray-500">
                                        {candidateData.fullName ? candidateData.fullName.split(' ').map(n => n[0]).join('').toUpperCase() : 'U'}
                                    </span>
                                )}
                            </div>
                            <div>
                                {isOpenToWork && (
                                    <div className="text-xs text-gray-600">Open to work</div>
                                )}
                                <div className="flex items-center gap-2">
                                    <div className="text-xl font-semibold text-gray-900">{candidateData.fullName}</div>
                                    {candidateData.hpcsaRegNo && typeof candidateData.hpcsaRegNo === 'string' && candidateData.hpcsaRegNo.trim().length > 0 && (
                                        <img
                                            src="/SealCheck.svg"
                                            alt={candidateData.hpcsaVerified ? "HPCSA Verified" : "HPCSA Pending Verification"}
                                            className={`w-5 h-5 transition-all ${
                                                candidateData.hpcsaVerified 
                                                    ? 'opacity-100' 
                                                    : 'opacity-30 grayscale'
                                            }`}
                                            title={candidateData.hpcsaVerified ? "HPCSA Verified" : "HPCSA Verification Pending"}
                                        />
                                    )}
                                </div>
                                {candidateData.headline && candidateData.headline.trim() && (
                                    <p className="text-sm text-gray-600 font-medium">{candidateData.headline}</p>
                                )}
                                <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                                    {/* <span>{candidateData.email}</span> */}
                                    {/* <span className="text-gray-300">|</span> */}
                                    <span>{candidateData.location}</span>
                                    {candidateData.linkedinUrl && (
                                        <a href={candidateData.linkedinUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:underline">
                                            <LinkedInIcon />
                                            <span>LinkedIn</span>
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>
                        {/* Only show hiring step buttons if jobId is present */}
                        {jobId && (
                            <div className="flex items-center gap-3 employer-candidate-header-actions">
                                <button 
                                    onClick={handleDeclineApplicant}
                                    // disabled={isDisqualified}
                                    className={`px-4 py-2 rounded-full text-sm ${
                                        isDisqualified 
                                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                                            : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                                    }`}
                                >
                                    {isDisqualified ? 'Disqualified' : 'Disqualify'}
                                </button>
                                {isDisqualified && (
                                    <select
                                        value=""
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            if (val === "") return;
                                            const idx = parseInt(val, 10);
                                            if (!Number.isNaN(idx)) {
                                                handleRestoreFromDisqualified(idx);
                                            }
                                            e.target.value = "";
                                        }}
                                        className="px-4 py-2 rounded-full text-sm bg-white border border-gray-300 text-gray-800 hover:bg-gray-50"
                                        title="Restore candidate from Disqualified"
                                    >
                                        <option value="">Restore</option>
                                        {steps.map((s, idx) => (
                                            <option key={s.key} value={idx}>{s.label}</option>
                                        ))}
                                    </select>
                                )}
                                <button 
                                    onClick={handleNextStep}
                                    disabled={currentStepIndex >= steps.length - 1 || isDisqualified}
                                    className={`px-4 py-2 rounded-full text-sm flex items-center gap-1 ${
                                        currentStepIndex >= steps.length - 1 || isDisqualified
                                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                            : 'bg-black text-white hover:bg-gray-800'
                                    }`}
                                >
                                    <span>Next Steps</span>
                                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M9 18l6-6-6-6" /></svg>
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Hiring steps - only show if jobId is present */}
                    {jobId && (
                        <div className="mt-6">
                            <div className="text-sm text-gray-600 mb-2">Hiring step</div>
                            <div className="grid grid-cols-5 gap-6 employer-candidate-hiring-grid">
                                {steps.map((s, idx) => {
                                    const isActive = !isDisqualified && idx <= currentStepIndex;
                                    return (
                                        <div key={s.key}>
                                            <div className="text-xs text-gray-600 mb-1">{s.label}</div>
                                            <div className={`h-1 rounded-full ${
                                                isDisqualified 
                                                    ? 'bg-red-500' 
                                                    : isActive
                                                        ? 'bg-black' 
                                                        : 'bg-gray-200'
                                            }`}></div>
                                        </div>
                                    );
                                })}
                            </div>
                            {isDisqualified && (
                                <div className="mt-2 text-xs text-red-600 font-medium">Candidate has been disqualified</div>
                            )}
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 employer-candidate-main-grid">
                    {/* Left Column */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* CV, Cover Letter, and Screening Questions - all in one block - only show when viewing from a job listing */}
                        {jobId && applicationData && (
                            <div className="bg-white rounded-xl border border-gray-200 p-6 employer-candidate-card">
                                {/* CV and Cover Letter side by side with individual outlines */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                    {/* CV */}
                                    <div className="border border-gray-200 rounded-lg p-4">
                                        <div className="text-lg font-semibold text-gray-900 mb-3">CV</div>
                                        {applicationData.cv_url ? (
                                            <div className="flex items-center gap-2">
                                                <a
                                                    href={applicationData.cv_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-sm text-blue-600 hover:text-blue-800 hover:underline truncate flex-1 min-w-0"
                                                >
                                                    {applicationData.cv_url}
                                                </a>
                                                <a
                                                    href="#"
                                                    onClick={async (e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        try {
                                                            const response = await fetch(applicationData.cv_url);
                                                            const blob = await response.blob();
                                                            const url = window.URL.createObjectURL(blob);
                                                            const link = document.createElement('a');
                                                            link.href = url;
                                                            link.download = applicationData.cv_filename || 'CV.pdf';
                                                            link.style.display = 'none';
                                                            document.body.appendChild(link);
                                                            link.click();
                                                            document.body.removeChild(link);
                                                            window.URL.revokeObjectURL(url);
                                                        } catch (error) {
                                                            console.error('Error downloading CV:', error);
                                                            // Fallback: try direct download attribute
                                                            const fallbackLink = document.createElement('a');
                                                            fallbackLink.href = applicationData.cv_url;
                                                            fallbackLink.download = applicationData.cv_filename || 'CV.pdf';
                                                            fallbackLink.style.display = 'none';
                                                            document.body.appendChild(fallbackLink);
                                                            fallbackLink.click();
                                                            document.body.removeChild(fallbackLink);
                                                        }
                                                        return false;
                                                    }}
                                                    className="cursor-pointer hover:text-gray-700 transition-colors"
                                                    title="Download CV"
                                                >
                                                    <svg className="w-4 h-4 text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                                    </svg>
                                                </a>
                                            </div>
                                        ) : (
                                            <p className="text-gray-500 italic text-sm">No CV uploaded</p>
                                        )}
                                    </div>

                                    {/* Cover Letter */}
                                    <div className="border border-gray-200 rounded-lg p-4">
                                        <div className="text-lg font-semibold text-gray-900 mb-3">Cover Letter</div>
                                        {applicationData.cover_letter_url ? (
                                            <div className="flex items-center gap-2">
                                                <a
                                                    href={applicationData.cover_letter_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-sm text-blue-600 hover:text-blue-800 hover:underline truncate flex-1 min-w-0"
                                                >
                                                    {applicationData.cover_letter_url}
                                                </a>
                                                <a
                                                    href="#"
                                                    onClick={async (e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        try {
                                                            const response = await fetch(applicationData.cover_letter_url);
                                                            const blob = await response.blob();
                                                            const url = window.URL.createObjectURL(blob);
                                                            const link = document.createElement('a');
                                                            link.href = url;
                                                            link.download = applicationData.cover_letter_filename || 'Cover Letter.pdf';
                                                            link.style.display = 'none';
                                                            document.body.appendChild(link);
                                                            link.click();
                                                            document.body.removeChild(link);
                                                            window.URL.revokeObjectURL(url);
                                                        } catch (error) {
                                                            console.error('Error downloading Cover Letter:', error);
                                                            // Fallback: try direct download attribute
                                                            const fallbackLink = document.createElement('a');
                                                            fallbackLink.href = applicationData.cover_letter_url;
                                                            fallbackLink.download = applicationData.cover_letter_filename || 'Cover Letter.pdf';
                                                            fallbackLink.style.display = 'none';
                                                            document.body.appendChild(fallbackLink);
                                                            fallbackLink.click();
                                                            document.body.removeChild(fallbackLink);
                                                        }
                                                        return false;
                                                    }}
                                                    className="cursor-pointer hover:text-gray-700 transition-colors"
                                                    title="Download Cover Letter"
                                                >
                                                    <svg className="w-4 h-4 text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                                    </svg>
                                                </a>
                                            </div>
                                        ) : (
                                            <p className="text-gray-500 italic text-sm">No cover letter uploaded</p>
                                        )}
                                    </div>
                                </div>

                                {/* Screening Questions - in the same block */}
                                {jobListingData ? (
                                    <div className="border-t border-gray-200 pt-6">
                                        <div className="text-lg font-semibold text-gray-900 mb-3">Screening questions</div>
                                        <div className="space-y-4">
                                            {/* Custom screening questions */}
                                            {jobListingData.screening_questions_list && Array.isArray(jobListingData.screening_questions_list) && jobListingData.screening_questions_list.length > 0 && (
                                                <>
                                                    {jobListingData.screening_questions_list.map((questionObj, qIndex) => {
                                                        const answer = applicationData.screening_answers?.[qIndex] || applicationData.screening_answers?.[`${qIndex}`];
                                                        const questionType = questionObj.type || (questionObj.options ? "multiple-choice" : "text");
                                                        return (
                                                            <div key={qIndex} className="border-b border-gray-100 pb-4 last:border-b-0 last:pb-0">
                                                                <div className="text-sm font-medium text-gray-900 mb-2">{questionObj.question}</div>
                                                                <div className="text-sm text-gray-700">
                                                                    {questionType === "text" ? (
                                                                        <div>
                                                                            <span className="font-medium block mb-1">Answer:</span>
                                                                            <div className="bg-gray-50 rounded-lg p-3 whitespace-pre-wrap">
                                                                                {answer || 'Not answered'}
                                                                            </div>
                                                                        </div>
                                                                    ) : (
                                                                        <span>Answer: <span className="font-medium">{answer || 'Not answered'}</span></span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </>
                                            )}

                                            {/* Drivers License Question */}
                                            {jobListingData.drivers_license_required && (
                                                <div className="border-b border-gray-100 pb-4 last:border-b-0 last:pb-0">
                                                    <div className="text-sm font-medium text-gray-900 mb-2">Do you have a valid driver's license?</div>
                                                    <div className="text-sm text-gray-700">
                                                        Answer: <span className="font-medium">{applicationData.drivers_license_answer || 'Not answered'}</span>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Visa Sponsorship Question */}
                                            {jobListingData.visa_sponsorship_offered && (
                                                <div className="border-b border-gray-100 pb-4 last:border-b-0 last:pb-0">
                                                    <div className="text-sm font-medium text-gray-900 mb-2">Do you require visa sponsorship?</div>
                                                    <div className="text-sm text-gray-700">
                                                        Answer: <span className="font-medium">{applicationData.visa_sponsorship_answer || 'Not answered'}</span>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Legacy screening answers (for backward compatibility) */}
                                            {applicationData.eligible_to_work !== null && (
                                                <div className="border-b border-gray-100 pb-4 last:border-b-0 last:pb-0">
                                                    <div className="text-sm font-medium text-gray-900 mb-2">Are you legally eligible to work in South Africa?</div>
                                                    <div className="text-sm text-gray-700">
                                                        Answer: <span className="font-medium">{applicationData.eligible_to_work || 'Not answered'}</span>
                                                    </div>
                                                </div>
                                            )}
                                            {applicationData.clinical_experience !== null && (
                                                <div className="border-b border-gray-100 pb-4 last:border-b-0 last:pb-0">
                                                    <div className="text-sm font-medium text-gray-900 mb-2">Do you have clinical experience?</div>
                                                    <div className="text-sm text-gray-700">
                                                        Answer: <span className="font-medium">{applicationData.clinical_experience || 'Not answered'}</span>
                                                    </div>
                                                </div>
                                            )}
                                            {applicationData.available_on_site !== null && (
                                                <div className="border-b border-gray-100 pb-4 last:border-b-0 last:pb-0">
                                                    <div className="text-sm font-medium text-gray-900 mb-2">Are you available to work on-site?</div>
                                                    <div className="text-sm text-gray-700">
                                                        Answer: <span className="font-medium">{applicationData.available_on_site || 'Not answered'}</span>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Show message if no screening questions */}
                                            {(!jobListingData.screening_questions_list || !Array.isArray(jobListingData.screening_questions_list) || jobListingData.screening_questions_list.length === 0) &&
                                             !jobListingData.drivers_license_required &&
                                             !jobListingData.visa_sponsorship_offered &&
                                             applicationData.eligible_to_work === null &&
                                             applicationData.clinical_experience === null &&
                                             applicationData.available_on_site === null && (
                                                <p className="text-gray-500 italic text-sm">No screening questions for this job listing</p>
                                            )}
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        )}

                        {/* About */}
                        <div className="bg-white rounded-xl border border-gray-200 p-6 employer-candidate-card">
                            <div className="text-lg font-semibold text-gray-900 mb-3">About</div>
                            <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{candidateData.aboutText || 'No about information available.'}</p>
                        </div>

                        {/* Skills */}
                        <div className="bg-white rounded-xl border border-gray-200 p-6 employer-candidate-card">
                            <div className="text-lg font-semibold text-gray-900 mb-3">Skills</div>
                            {candidateData.skills?.length ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {[0, 1].map(col => (
                                        <ul key={col} className="space-y-2">
                                            {candidateData.skills
                                                .slice(
                                                    col === 0 ? 0 : Math.ceil(candidateData.skills.length / 2), 
                                                    col === 0 ? Math.ceil(candidateData.skills.length / 2) : candidateData.skills.length
                                                )
                                                .map((skill, index) => (
                                                    <li key={`${col}-${index}`} className="flex items-center text-gray-700">
                                                        <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mr-3"></span>
                                                        <span>
                                                            {typeof skill === 'string' ? skill : skill.name}
                                                            {typeof skill === 'object' && skill.level && (
                                                                <span className="text-sm text-gray-500 ml-2">({skill.level})</span>
                                                            )}
                                                        </span>
                                                    </li>
                                                ))}
                                        </ul>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-gray-500 italic">No skills added yet.</p>
                            )}
                        </div>

                        {/* Experience */}
                        <div className="bg-white rounded-xl border border-gray-200 p-6 employer-candidate-card">
                            <div className="text-lg font-semibold text-gray-900 mb-3">Experience</div>
                            {candidateData.workExperiences?.length ? (
                                <div className="space-y-6">
                                    {candidateData.workExperiences.map((experience, index) => (
                                        <div key={index} className="border-l-4 border-gray-900 pl-4">
                                            <div className="font-semibold text-gray-900">{experience.title || '[Role]'}</div>
                                            <div className="text-gray-600">{experience.company || 'Company Name'}</div>
                                            <div className="text-gray-500 text-sm">
                                                {experience.startDate && (experience.endDate || experience.isCurrent) ? `${experience.startDate} to ${experience.isCurrent ? 'Present' : experience.endDate}` : 'Date not specified'}
                                            </div>
                                            {experience.description && (
                                                <p className="text-gray-700 mt-2 whitespace-pre-wrap">
                                                    {experience.description}
                                                </p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-gray-500 italic">No work experience added yet.</p>
                            )}
                        </div>

                        {/* Education */}
                        <div className="bg-white rounded-xl border border-gray-200 p-6 employer-candidate-card">
                            <div className="text-lg font-semibold text-gray-900 mb-3">Education</div>
                            {candidateData.educations?.length ? (
                                <div className="space-y-6">
                                    {candidateData.educations.map((education, index) => (
                                        <div key={index} className="border-l-4 border-gray-900 pl-4">
                                            <div className="font-semibold text-gray-900">{education.fieldOfStudy || education.degree || '[Course name]'}</div>
                                            <div className="text-gray-600">{education.school || 'Institution Name'}</div>
                                            <div className="text-gray-500 text-sm">
                                                {education.startDate && (education.endDate || education.isCurrent) ? `${education.startDate} - ${education.isCurrent ? 'Present' : education.endDate}` : 'Date not specified'}
                                            </div>
                                            {education.description && (
                                                <p className="text-gray-700 mt-2 whitespace-pre-wrap">{education.description}</p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-gray-500 italic">No education information added yet.</p>
                            )}
                        </div>

                        {/* Certifications */}
                        <div className="bg-white rounded-xl border border-gray-200 p-6">
                            <div className="text-lg font-semibold text-gray-900 mb-3">Certifications</div>
                            {candidateData.certifications?.length ? (
                                <div className="space-y-4">
                                    {candidateData.certifications.map((certification, index) => (
                                        <div key={index} className="border-l-4 border-gray-900 pl-4">
                                            <div className="font-semibold text-gray-900">{certification.courseName || '[Course name]'}</div>
                                            <div className="text-gray-600">{certification.issuingOrg || 'Institution Name'}</div>
                                            <div className="text-gray-500 text-sm">{certification.issueDate ? `Issued: ${certification.issueDate}` : 'Issued: date'}</div>
                                            {certification.credentialId && (
                                                <div className="text-gray-500 text-sm">Credential ID: {certification.credentialId}</div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-gray-500 italic">No certifications added yet.</p>
                            )}
                        </div>
                    </div>

                    {/* Right Column */}
                    <div className="space-y-6">
                        {/* Connect with Candidate - show when browsing all candidates (no job context) */}
                        {!jobId && (
                            <div className="bg-white rounded-xl border border-gray-200 p-6">
                                <div className="text-lg font-semibold text-gray-900 mb-3">Connect with Candidate</div>
                                <button 
                                    onClick={handleSendMessage} 
                                    className="w-full bg-black text-white px-5 py-3 rounded-full text-sm font-semibold hover:bg-gray-900"
                                >
                                    Send message
                                </button>
                            </div>
                        )}

                        {/* Connect with Applicant - show when viewing from a specific job */}
                        {jobId && (
                            <div className="bg-white rounded-xl border border-gray-200 p-6">
                                <div className="text-lg font-semibold text-gray-900 mb-3">Connect with Applicant</div>
                                <div className="flex items-center gap-3 mb-3 -ml-2">
                                    <button 
                                        onClick={handleDeclineApplicant}
                                        disabled={isDisqualified}
                                        className={`flex-1 border border-gray-300 bg-white text-gray-900 px-4 py-1.5 rounded-full text-xs font-medium hover:bg-gray-50 flex items-center justify-center gap-2 whitespace-nowrap ${
                                            isDisqualified ? 'opacity-50 cursor-not-allowed' : ''
                                        }`}
                                    >
                                        <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                        <span>Decline Applicant</span>
                                    </button>
                                    <button 
                                        onClick={handleAcceptApplicant}
                                        disabled={isDisqualified || currentStepIndex >= 1}
                                        className={`flex-1 border border-gray-300 bg-white text-gray-900 px-4 py-1.5 rounded-full text-xs font-medium hover:bg-gray-50 flex items-center justify-center gap-2 whitespace-nowrap ${
                                            isDisqualified || currentStepIndex >= 1 ? 'opacity-50 cursor-not-allowed' : ''
                                        }`}
                                    >
                                        <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        <span>Accept Applicant</span>
                                    </button>
                                </div>
                                <button 
                                    onClick={handleSendMessage} 
                                    className="w-full bg-black text-white px-5 py-3 rounded-full text-sm font-semibold hover:bg-gray-900"
                                >
                                    Send message
                                </button>
                            </div>
                        )}

                        {/* Roles open to */}
                        <div className="bg-white rounded-xl border border-gray-200 p-6">
                            <div className="text-lg font-semibold text-gray-900 mb-3">Roles open to:</div>
                            {candidateData.rolesOpenTo?.length ? (
                                <div className="flex flex-wrap gap-2">
                                    {candidateData.rolesOpenTo.map((role, index) => (
                                        <span key={index} className="px-3 py-1 rounded-full bg-gray-100 text-gray-800 text-sm">{role}</span>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-gray-500 italic">No roles specified yet.</p>
                            )}
                        </div>

                        {/* Languages */}
                        <div className="bg-white rounded-xl border border-gray-200 p-6">
                            <div className="text-lg font-semibold text-gray-900 mb-3">Languages spoken</div>
                            {candidateData.languages?.length ? (
                                <ul className="space-y-2">
                                    {candidateData.languages.map((language, index) => (
                                        <li key={index} className="flex items-center text-gray-700">
                                            <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mr-3"></span>
                                            {language}
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="text-gray-500 italic">No languages added yet.</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Disqualify Modal */}
            {showDisqualifyModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
                        {/* Modal Header */}
                        <div className="flex items-center justify-between p-6 border-b border-gray-200">
                            <h2 className="text-xl font-semibold text-gray-900">
                                Disqualify - {candidateData?.fullName || 'Candidate'}
                            </h2>
                            <button
                                onClick={() => setShowDisqualifyModal(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="p-6 space-y-6">
                            {/* Reason Section */}
                            <div>
                                <label className="block text-sm font-medium text-gray-900 mb-2">
                                    Reason
                                </label>
                                <select
                                    value={disqualifyReason}
                                    onChange={(e) => setDisqualifyReason(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent appearance-none bg-white"
                                    style={{
                                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
                                        backgroundPosition: 'right 0.5rem center',
                                        backgroundRepeat: 'no-repeat',
                                        backgroundSize: '1.5em 1.5em',
                                        paddingRight: '2.5rem'
                                    }}
                                >
                                    <option value="Experience suitability is not suitable">Experience suitability is not suitable</option>
                                    <option value="Skills do not match requirements">Skills do not match requirements</option>
                                    <option value="Location preference mismatch">Location preference mismatch</option>
                                    <option value="Salary expectations not aligned">Salary expectations not aligned</option>
                                    <option value="Availability does not meet requirements">Availability does not meet requirements</option>
                                    <option value="Other">Other</option>
                                </select>
                                <p className="text-xs text-gray-500 mt-1">
                                    *This information will remain confidential from the candidate
                                </p>
                            </div>

                            {/* Rejection Section */}
                            <div>
                                <label className="block text-sm font-medium text-gray-900 mb-3">
                                    Rejection
                                </label>
                                <div className="space-y-3">
                                    <label className="flex items-center cursor-pointer">
                                        <input
                                            type="radio"
                                            name="notification"
                                            checked={sendNotification}
                                            onChange={() => setSendNotification(true)}
                                            className="w-4 h-4 text-black border-gray-300 focus:ring-black"
                                        />
                                        <span className="ml-2 text-sm text-gray-700">Send an email notification</span>
                                    </label>
                                    <label className="flex items-center cursor-pointer">
                                        <input
                                            type="radio"
                                            name="notification"
                                            checked={!sendNotification}
                                            onChange={() => setSendNotification(false)}
                                            className="w-4 h-4 text-black border-gray-300 focus:ring-black"
                                        />
                                        <span className="ml-2 text-sm text-gray-700">Do not notify</span>
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
                            <button
                                onClick={() => setShowDisqualifyModal(false)}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-900 bg-white hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDisqualifyConfirm}
                                className="px-4 py-2 bg-black text-white rounded-lg text-sm font-medium hover:bg-gray-800"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Restore Status Modal */}
            {showRestoreStatusModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200">
                            <h2 className="text-xl font-semibold text-gray-900">Update candidate status</h2>
                            <button
                                type="button"
                                onClick={() => {
                                    setShowRestoreStatusModal(false);
                                    setPendingRestoreStepIndex(null);
                                    setRestoreStatusError('');
                                }}
                                className="text-gray-400 hover:text-gray-600"
                                aria-label="Close"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="p-6 space-y-4">
                            <div className="text-sm text-gray-700">
                                This will update the candidate&apos;s hiring stage. Choose whether to send them an email about this change, or update the stage only.
                            </div>
                            {restoreStatusError && (
                                <div className="text-sm text-red-600 font-medium">
                                    {restoreStatusError}
                                </div>
                            )}
                        </div>

                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3 p-6 border-t border-gray-200">
                            <button
                                type="button"
                                onClick={() => confirmRestoreFromDisqualified(false)}
                                className="px-4 py-2 rounded-full border border-gray-300 text-gray-800 bg-white hover:bg-gray-50 transition-colors text-sm font-medium"
                            >
                                Update without mailing
                            </button>
                            <button
                                type="button"
                                onClick={() => confirmRestoreFromDisqualified(true)}
                                className="px-4 py-2 rounded-full bg-black text-white hover:bg-gray-800 transition-colors text-sm font-medium"
                            >
                                Update &amp; Email
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Next step — notification choice (close X to cancel) */}
            {showNextStepConfirmModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200">
                            <h2 className="text-xl font-semibold text-gray-900">Advance to next step</h2>
                            <button
                                type="button"
                                onClick={() => setShowNextStepConfirmModal(false)}
                                className="text-gray-400 hover:text-gray-600"
                                aria-label="Close"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="p-6 space-y-4">
                            <div className="text-sm text-gray-700">
                                Move this candidate to the next hiring stage. Choose whether to notify them by email, or advance without sending an email.
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3 p-6 border-t border-gray-200">
                            <button
                                type="button"
                                onClick={() => confirmAdvanceNextStep(false)}
                                className="px-4 py-2 rounded-full border border-gray-300 text-gray-800 bg-white hover:bg-gray-50 transition-colors text-sm font-medium"
                            >
                                Update without mailing
                            </button>
                            <button
                                type="button"
                                onClick={() => confirmAdvanceNextStep(true)}
                                className="px-4 py-2 rounded-full bg-black text-white hover:bg-gray-800 transition-colors text-sm font-medium"
                            >
                                Update &amp; Notify
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
        </>
    );
};

export default CandidateProfile;
